import React, { useEffect, useReducer } from 'react'
import { flowElement, flowTail } from './Workflow/WorkflowElements'
import WorkflowModal from './WorkflowModal'
import BootstrapAlert from '../element/BootstrapAlert'

function checkWorkflowValid (members, allowGeneralsOnly, approverInfo) {
  if (approverInfo && members.length < approverInfo.level + 1) {
    return '承認済みアカウントが最終承認者となるような変更はできません。'
  }
  if (allowGeneralsOnly) {
    return null
  } else {
    let includeManager = false
    if (members.length > 0) {
      // 最終承認レベルに管理者がひとりもいないときにエラー
      includeManager = members[members.length - 1].reduce((result, member) => result || member.is_manager, false)
    } else {
      // メンバーがいないのでエラー
      includeManager = false
    }

    if (!includeManager) {
      return '最終承認者には管理メンバーを1人以上設定してください。'
    } else {
      return null
    }
  }
}

function checkApproverRemoved (members, approverInfo) {
  if (approverInfo) {
    const approvers = approverInfo.level < members.length ? members[approverInfo.level] : []
    return !approvers.find((aprv) => aprv.id === approverInfo.userIdToCheck)
  } else {
    return false
  }
}

const membersStateReducer = (allowGeneralsOnly, setInvalidMessage, approverInfo) => (membersState, action) => {
  const newMembersState = {
    members: [...membersState.members],
    invalidMessage: ''
  }
  if (action.type === 'set') {
    const _members = (action.members ?? [])
      .map((member) => Array.isArray(member) ? member : null)
      .filter((member) => member)
    if (JSON.stringify(membersState.members) !== JSON.stringify(_members)) {
      newMembersState.members = _members
    } else {
      // メンバーに変更がないので元のStateを返す
      // （newMemersStateを返すと無限に更新されてしまう）
      return membersState
    }
  } else if (action.type === 'insert') {
    if (action.users && action.users.length > 0) {
      newMembersState.members.splice(action.index, 0, action.users)
    }
  } else if (action.type === 'edit') {
    if (action.users && action.users.length > 0) {
      newMembersState.members.splice(action.index, 1, action.users)
    }
  } else if (action.type === 'remove') {
    newMembersState.members = newMembersState.members.filter((member, j) => action.index !== j)
  }

  newMembersState.invalidMessage = checkWorkflowValid(newMembersState.members, allowGeneralsOnly, approverInfo)
  if (setInvalidMessage) {
    setInvalidMessage(newMembersState.invalidMessage)
  }

  return newMembersState
}

const modalStateReducer = (modalState, action) => {
  if (action.type === 'insert') {
    if (modalState.isOpen) {
      return modalState
    } else {
      return {
        mode: action.type,
        addIndex: action.addIndex,
        isOpen: true,
        getSelectingUsers: (members) => null
      }
    }
  } else if (action.type === 'edit') {
    if (modalState.isOpen) {
      return modalState
    } else {
      return {
        mode: action.type,
        addIndex: action.addIndex,
        isOpen: true,
        getSelectingUsers: (members) => action.addIndex > -1 ? members[action.addIndex] : null
      }
    }
  } else if (action.type === 'default') {
    return {
      mode: 'default',
      addIndex: -1,
      isOpen: false,
      getSelectingUsers: (members) => null
    }
  }
}

export default function Workflow (
  generalUsers, // PropTypes.arrayOf(PropTypes.object),
  managers, // PropTypes.arrayOf(PropTypes.object),
  initialMembers, // PropTypes.arrayOf(PropTypes.object),
  firstNodeName, // PropTypes.string,
  isViewMode, // PropTypes.bool,
  allowGeneralsOnly, // PropTypes.bool,
  workflowName = null, // PropTypes.string
  setInvalidMessage = null,
  approverInfo = null, // PropTypes.object,
  fixFirstApprover = false
) {
  const [membersState, membersStateDispatch] = useReducer(
    membersStateReducer(allowGeneralsOnly, setInvalidMessage, approverInfo),
    {
      members: [],
      invalidMessage: ''
    }
  )
  const [modalState, modalStateDispatch] = useReducer(
    modalStateReducer,
    { mode: 'default', addIndex: -1, isOpen: false, getSelectingUsers: (members) => null }
  )

  useEffect(() => {
    membersStateDispatch({
      type: 'set',
      members: initialMembers
    })
  }, [initialMembers])

  function onAddButtonClick (i) {
    return () => {
      modalStateDispatch({ type: 'insert', addIndex: i })
    }
  }

  function onEditButtonClick (i) {
    return () => {
      modalStateDispatch({ type: 'edit', addIndex: i })
    }
  }

  function onRemoveButtonClick (i) {
    return () => {
      membersStateDispatch({ type: 'remove', index: i })
    }
  }

  function getOnModalSubmit (_modalState) {
    return (users) => {
      membersStateDispatch({
        type: _modalState.mode,
        index: _modalState.addIndex,
        users: users
      })
      modalStateDispatch({ type: 'default' })
    }
  }

  return (
    <>
      <div className="card card-theme text-center">
        <div className="card-header">
          {workflowName ?? `承認ルート（承認者：${membersState.members.length}人）`}
        </div>
        <div className={`card-body flow-col ${modalState.isOpen ? 'opacity3' : ''}`}>
          <div>
            {checkApproverRemoved(membersState.members, approverInfo) &&
              (
              <BootstrapAlert class='danger'>
                <div>現在あなたは承認者ですが、変更後は承認者ではなくなります。ご注意ください。</div>
              </BootstrapAlert>
              )}
            <button type="button" className="btn btn-lg bg-1 w-100 opacity10 mb-1" disabled>
              {firstNodeName}
            </button>
            {
              membersState.members.map((member, i) => {
                const firstFixed = fixFirstApprover && i === 0 // 最初の承認者を固定するときはi=0のときtrue
                const levelFixed = member.reduce((result, user) => result || user.isApproved, false) // レベルが承認済みのときは固定
                return flowElement(
                  i,
                  member,
                  onAddButtonClick(i),
                  onEditButtonClick(i),
                  onRemoveButtonClick(i),
                  !levelFixed,
                  firstFixed || isViewMode || levelFixed,
                  !(isViewMode || levelFixed)
                )
              })
            }
            { flowTail(onAddButtonClick(membersState.members.length), !modalState.isOpen, isViewMode) }
          </div>
        </div>
      </div>
      {
        WorkflowModal(
          modalState.isOpen,
          generalUsers.filter((u) => u.in_use),
          managers.filter((u) => u.in_use),
          modalState.getSelectingUsers(membersState.members),
          getOnModalSubmit(modalState)
        )
      }
      <input type="text" name="members_json" className="d-none" id="members-json" readOnly value={JSON.stringify(membersState.members, ['id'])} />
    </>
  )
}
